import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// ── Map a raw Printify product to our display shape ───────────────────────────
function mapPrintify(p: any) {
  const enabled  = (p.variants ?? []).filter((v: any) => v.is_enabled);
  const minPrice = enabled.length > 0
    ? Math.min(...enabled.map((v: any) => v.price)) / 100
    : 0;

  const defaultImg = (p.images ?? []).find((i: any) => i.is_default)?.src
    ?? p.images?.[0]?.src
    ?? '/images/OG-Tech Logo.png';

  return {
    id:           p.id,
    category:     'Merch',
    name:         p.title,
    shortDesc:    (p.description ?? '').replace(/<[^>]+>/g, '').slice(0, 100),
    price:        minPrice,
    image:        defaultImg,
    affiliateUrl: null as string | null,
  };
}

const partCategories = ['All Parts', 'Gauges', 'Boost', 'AN Fittings', 'Welding', 'Diagnostics'];
const topCategories  = ['All', 'Merch', 'In Stock', 'Parts'];

const MAKES = [
  'Acura','Audi','BMW','Buick','Cadillac','Chevrolet','Chrysler','Dodge',
  'Ford','GMC','Honda','Hyundai','Infiniti','Jeep','Kia','Lexus','Mazda',
  'Mercedes-Benz','Mitsubishi','Nissan','Pontiac','Porsche','Subaru',
  'Toyota','Volkswagen','Volvo',
];

const selectStyle: React.CSSProperties = {
  fontFamily: 'DM Mono', fontSize: 12, letterSpacing: 1,
  padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--border)', background: '#0d1018',
  color: 'var(--text)', cursor: 'pointer', minWidth: 110,
};

const inputStyle: React.CSSProperties = {
  fontFamily: 'DM Mono', fontSize: 12, letterSpacing: 1,
  padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--border)', background: '#0d1018',
  color: 'var(--text)', minWidth: 160, outline: 'none',
};

function InStockModal({ p, onClose }: { p: any; onClose: () => void }) {
  const imgs: string[] = (p.images && p.images.length > 0) ? p.images : (p.image ? [p.image] : []);
  const [idx, setIdx] = useState(0);

  const arrowStyle: React.CSSProperties = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 16, zIndex: 2,
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#0e1118', border: '1px solid var(--border)', borderRadius: 16,
        maxWidth: 680, width: '100%', maxHeight: '90vh', overflow: 'auto',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Close */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 16px 0' }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid var(--border)', borderRadius: '50%',
            width: 32, height: 32, cursor: 'pointer', color: 'var(--muted)', fontSize: 14,
          }}>✕</button>
        </div>

        {/* Image carousel */}
        <div style={{ position: 'relative', background: '#090c12', aspectRatio: '4/3', overflow: 'hidden' }}>
          {imgs.length > 0
            ? <img src={imgs[idx]} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 24 }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontFamily: 'DM Mono', fontSize: 13 }}>NO IMAGE</div>
          }
          {imgs.length > 1 && (
            <>
              <button onClick={() => setIdx(i => (i - 1 + imgs.length) % imgs.length)} style={{ ...arrowStyle, left: 12 }}>‹</button>
              <button onClick={() => setIdx(i => (i + 1) % imgs.length)} style={{ ...arrowStyle, right: 12 }}>›</button>
              <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6 }}>
                {imgs.map((_, i) => (
                  <div key={i} onClick={() => setIdx(i)} style={{
                    width: i === idx ? 20 : 7, height: 7, borderRadius: 4,
                    background: i === idx ? 'var(--green)' : 'rgba(255,255,255,0.3)',
                    cursor: 'pointer', transition: 'width 0.2s',
                  }} />
                ))}
              </div>
              {/* Thumbnail strip */}
              <div style={{ position: 'absolute', bottom: 32, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6, padding: '0 12px' }}>
              </div>
            </>
          )}
        </div>

        {/* Thumbnail row */}
        {imgs.length > 1 && (
          <div style={{ display: 'flex', gap: 8, padding: '10px 20px', overflowX: 'auto' }}>
            {imgs.map((src, i) => (
              <div key={i} onClick={() => setIdx(i)} style={{
                flexShrink: 0, width: 56, height: 56, borderRadius: 8, overflow: 'hidden',
                border: `2px solid ${i === idx ? 'var(--green)' : 'var(--border)'}`,
                cursor: 'pointer', background: '#090c12', transition: 'border-color 0.15s',
              }}>
                <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        )}

        {/* Info */}
        <div style={{ padding: '20px 24px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 3, color: 'var(--green)', textTransform: 'uppercase' }}>{p.category}</div>
            {p.brand && <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 2, color: 'var(--muted)', textTransform: 'uppercase' }}>{p.brand}</div>}
          </div>
          <div style={{ fontFamily: 'DM Sans', fontSize: 22, fontWeight: 700 }}>{p.name}</div>
          {p.desc && <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, margin: 0 }}>{p.desc}</p>}
          <div style={{ fontFamily: 'DM Sans', fontSize: 26, fontWeight: 900, color: 'var(--green)', marginTop: 4 }}>
            ${Number(p.price).toFixed(2)}
          </div>
          <div style={{ marginTop: 8, padding: '16px 20px', background: 'rgba(34,255,122,0.06)', border: '1px solid rgba(34,255,122,0.2)', borderRadius: 10 }}>
            <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 3, color: 'var(--green)', textTransform: 'uppercase', marginBottom: 6 }}>To Order</div>
            <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 600 }}>(210) 689-1550</div>
            <div style={{ fontFamily: 'DM Mono', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Call or text — we'll get it handled</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InStockCard({ p, onOpen }: { p: any; onOpen: () => void }) {
  const imgs: string[] = (p.images && p.images.length > 0) ? p.images : (p.image ? [p.image] : []);
  const coverImg = imgs[0];

  return (
    <div onClick={onOpen} style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
      transition: 'border-color 0.2s, transform 0.2s', cursor: 'pointer',
    }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--green)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
    >
      <div style={{ aspectRatio: '1', background: '#090c12', overflow: 'hidden', position: 'relative' }}>
        {coverImg
          ? <img src={coverImg} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 16 }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontFamily: 'DM Mono', fontSize: 11 }}>NO IMAGE</div>
        }
        {imgs.length > 1 && (
          <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 10, padding: '2px 8px', fontFamily: 'DM Mono', fontSize: 9, color: '#fff', letterSpacing: 1 }}>
            +{imgs.length - 1} more
          </div>
        )}
      </div>
      <div style={{ padding: '16px 18px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 2, color: 'var(--green)', textTransform: 'uppercase' }}>{p.category}</div>
          {p.brand && <div style={{ fontFamily: 'DM Mono', fontSize: 9, letterSpacing: 1, color: 'var(--muted)', textTransform: 'uppercase' }}>{p.brand}</div>}
        </div>
        <div style={{ fontFamily: 'DM Sans', fontSize: 15, fontWeight: 700, letterSpacing: 0.3 }}>{p.name}</div>
        {p.desc && <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, flex: 1, margin: 0 }}>{p.desc}</p>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <div style={{ fontFamily: 'DM Sans', fontSize: 17, fontWeight: 700, color: 'var(--green)' }}>${Number(p.price).toFixed(2)}</div>
          <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 1, color: 'var(--green)', textTransform: 'uppercase' }}>View →</div>
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  const [activeTab, setActiveTab]       = useState('All');
  const [partTab, setPartTab]           = useState('All Parts');
  const [merch, setMerch]               = useState<any[]>([]);
  const [parts, setParts]               = useState<any[]>([]);
  const [loadingMerch, setLoadingMerch]       = useState(true);
  const [loadingParts, setLoadingParts]       = useState(true);
  const [customParts, setCustomParts]         = useState<any[]>([]);
  const [amazonParts, setAmazonParts]         = useState<any[]>([]);
  const [error, setError]                     = useState('');
  const [vehicleYear, setVehicleYear]   = useState('');
  const [vehicleMake, setVehicleMake]   = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [selectedPart, setSelectedPart] = useState<any>(null);

  useEffect(() => {
    fetch('/.netlify/functions/products')
      .then(r => r.json())
      .then((data: any[]) => { setMerch(data.map(mapPrintify)); setLoadingMerch(false); })
      .catch(() => { setError('Could not reach product server.'); setLoadingMerch(false); });

    fetch('/parts.json')
      .then(r => r.json())
      .then((data: any[]) => { setParts(data); setLoadingParts(false); })
      .catch(() => setLoadingParts(false));

    fetch('/.netlify/functions/custom-parts')
      .then(r => r.json())
      .then((data: any[]) => setCustomParts(data))
      .catch(() => {});

    fetch('/.netlify/functions/amazon-links')
      .then(r => r.json())
      .then((data: any[]) => setAmazonParts(data))
      .catch(() => {});
  }, []);

  const vehicleActive = !!(vehicleYear || vehicleMake || vehicleModel);

  const vehicleMatches = (p: any) => {
    if (!vehicleActive) return true;
    if (p.universal) return true;
    if (vehicleYear && p.minYear && parseInt(vehicleYear) < p.minYear) return false;
    return true;
  };

  const allParts = [...parts, ...amazonParts];
  const filteredParts = (partTab === 'All Parts' ? allParts : allParts.filter(p => p.category === partTab))
    .filter(vehicleMatches);

  const showMerch   = activeTab === 'All' || activeTab === 'Merch';
  const showInStock = activeTab === 'All' || activeTab === 'In Stock';
  const showParts   = activeTab === 'All' || activeTab === 'Parts';
  const loading   = loadingMerch || loadingParts;

  return (
    <div style={{ paddingTop: 64 }}>
      {selectedPart && <InStockModal p={selectedPart} onClose={() => setSelectedPart(null)} />}

      {/* Header */}
      <section style={{
        padding: '80px 32px 60px',
        borderBottom: '1px solid var(--border)',
        background: 'linear-gradient(180deg, rgba(0,240,255,0.04) 0%, transparent 100%)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            fontFamily: 'DM Mono', fontSize: 18, letterSpacing: 4,
            color: 'var(--teal)', textTransform: 'uppercase', marginBottom: 12,
          }}>
            OG-TECH LLC
          </div>
          <h1 style={{
            fontFamily: 'Bomber Dreams', fontSize: 'clamp(48px, 8vw, 80px)',
            letterSpacing: 3, marginBottom: 16,
          }}>
            Shop
          </h1>
          <p style={{ fontSize: 16, color: 'var(--muted)', maxWidth: 480, lineHeight: 1.7, margin: '0 auto' }}>
            Official merch and OG-TECH recommended parts. Built in San Antonio.
          </p>
          <img src="/images/OG-Tech Logo.png" alt="OG-TECH"
            style={{ width: 'clamp(300px, 40vw, 500px)', display: 'block', margin: '24px auto 0', filter: 'drop-shadow(0 0 30px rgba(0,240,255,0.35))' }} />
        </div>
      </section>

      {/* Top tabs */}
      <section style={{ padding: '32px 32px 0', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', maxWidth: 1200, margin: '0 auto' }}>
          {topCategories.map(cat => (
            <button key={cat} onClick={() => setActiveTab(cat)} style={{
              fontFamily: 'DM Mono', fontSize: 13, letterSpacing: 2,
              textTransform: 'uppercase', padding: '8px 22px', borderRadius: 6,
              border: '1px solid var(--border)', cursor: 'pointer', marginBottom: 32,
              background: activeTab === cat ? 'var(--teal)' : 'transparent',
              color: activeTab === cat ? '#000' : 'var(--muted)',
              transition: 'all 0.15s',
            }}>
              {cat}
            </button>
          ))}
        </div>
      </section>

      <section style={{ padding: '60px 32px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {error && (
            <div style={{ textAlign: 'center', padding: '60px 0', fontFamily: 'DM Mono', fontSize: 12, color: 'var(--pink)', letterSpacing: 1 }}>
              {error}
            </div>
          )}

          {/* ── MERCH ── */}
          {showMerch && (
            <div style={{ marginBottom: showParts ? 80 : 0 }}>
              {activeTab === 'All' && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontFamily: 'DM Mono', fontSize: 18, letterSpacing: 4, color: 'var(--pink)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Merch
                  </div>
                  <div style={{ width: 40, height: 2, background: 'var(--pink)' }} />
                </div>
              )}

              {loadingMerch ? (
                <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: 'DM Mono', fontSize: 13, color: 'var(--muted)', letterSpacing: 2 }}>
                  LOADING...
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
                  {merch.map(p => (
                    <Link key={p.id} to={`/product/${p.id}`} style={{
                      background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                      transition: 'border-color 0.2s, transform 0.2s',
                    }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--pink)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                    >
                      <div style={{ aspectRatio: '1', background: '#090c12', overflow: 'hidden' }}>
                        <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '16px 18px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 2, color: 'var(--pink)', textTransform: 'uppercase' }}>Merch</div>
                        <div style={{ fontFamily: 'DM Sans', fontSize: 16, fontWeight: 700, letterSpacing: 0.3 }}>{p.name}</div>
                        <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, flex: 1 }}>{p.shortDesc}</p>
                        <div style={{ fontFamily: 'DM Sans', fontSize: 17, fontWeight: 700, color: 'var(--teal)', marginTop: 6 }}>${p.price.toFixed(2)}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── IN STOCK ── */}
          {showInStock && customParts.length > 0 && (
            <div style={{ marginBottom: showParts ? 80 : 0 }}>
              {activeTab === 'All' && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontFamily: 'DM Mono', fontSize: 18, letterSpacing: 4, color: 'var(--green)', textTransform: 'uppercase', marginBottom: 8 }}>
                    In Stock
                  </div>
                  <div style={{ width: 40, height: 2, background: 'var(--green)' }} />
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
                {customParts.map(p => <InStockCard key={p.id} p={p} onOpen={() => setSelectedPart(p)} />)}
              </div>
            </div>
          )}

          {/* ── PARTS ── */}
          {showParts && (
            <div>
              {activeTab === 'All' && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontFamily: 'DM Mono', fontSize: 18, letterSpacing: 4, color: 'var(--teal)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Parts
                  </div>
                  <div style={{ width: 40, height: 2, background: 'var(--teal)' }} />
                </div>
              )}

              {/* Vehicle search */}
              <div style={{ marginBottom: 24, padding: '16px 20px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10 }}>
                <div style={{ fontFamily: 'DM Mono', fontSize: 11, letterSpacing: 3, color: 'var(--teal)', textTransform: 'uppercase', marginBottom: 14 }}>
                  Search by Vehicle
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select value={vehicleYear} onChange={e => setVehicleYear(e.target.value)} style={selectStyle}>
                    <option value="">Year</option>
                    {Array.from({ length: 36 }, (_, i) => 2025 - i).map(y => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                  <select value={vehicleMake} onChange={e => setVehicleMake(e.target.value)} style={selectStyle}>
                    <option value="">Make</option>
                    {MAKES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder="Model (optional)"
                    value={vehicleModel}
                    onChange={e => setVehicleModel(e.target.value)}
                    style={inputStyle}
                  />
                  {vehicleActive && (
                    <button
                      onClick={() => { setVehicleYear(''); setVehicleMake(''); setVehicleModel(''); }}
                      style={{ fontFamily: 'DM Mono', fontSize: 11, letterSpacing: 1, padding: '8px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {vehicleActive && (
                  <div style={{ marginTop: 10, fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 1, color: 'var(--muted)' }}>
                    Showing parts for: {[vehicleYear, vehicleMake, vehicleModel].filter(Boolean).join(' ')}
                  </div>
                )}
              </div>

              {/* Parts sub-tabs */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32 }}>
                {partCategories.map(cat => (
                  <button key={cat} onClick={() => setPartTab(cat)} style={{
                    fontFamily: 'DM Mono', fontSize: 11, letterSpacing: 2,
                    textTransform: 'uppercase', padding: '6px 16px', borderRadius: 6,
                    border: `1px solid ${partTab === cat ? 'var(--teal)' : 'var(--border)'}`,
                    background: partTab === cat ? 'var(--teal-dim)' : 'transparent',
                    color: partTab === cat ? 'var(--teal)' : 'var(--muted)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                    {cat}
                  </button>
                ))}
              </div>

              {loadingParts ? (
                <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: 'DM Mono', fontSize: 13, color: 'var(--muted)', letterSpacing: 2 }}>
                  LOADING...
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
                  {filteredParts.map(p => (
                    <a key={p.id} href={p.affiliateUrl} target="_blank" rel="noopener noreferrer" style={{
                      background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                      transition: 'border-color 0.2s, transform 0.2s',
                    }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--teal)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                    >
                      <div style={{ aspectRatio: '1', background: '#090c12', overflow: 'hidden' }}>
                        <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 16 }} />
                      </div>
                      <div style={{ padding: '16px 18px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 2, color: 'var(--teal)', textTransform: 'uppercase' }}>{p.category}</div>
                          <div style={{ fontFamily: 'DM Mono', fontSize: 9, letterSpacing: 1, color: 'var(--muted)', textTransform: 'uppercase' }}>{p.brand}</div>
                        </div>
                        <div style={{ fontFamily: 'DM Sans', fontSize: 15, fontWeight: 700, letterSpacing: 0.3 }}>{p.name}</div>
                        <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, flex: 1 }}>{p.desc}</p>
                        {vehicleActive && p.fitsNote && (
                          <div style={{ fontFamily: 'DM Mono', fontSize: 9, letterSpacing: 1, color: 'var(--teal)', marginTop: 4 }}>
                            ✓ {p.fitsNote}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                          <div style={{ fontFamily: 'DM Sans', fontSize: 17, fontWeight: 700, color: 'var(--teal)' }}>${p.price.toFixed(2)}</div>
                          <div style={{ fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 1, color: 'var(--teal)', textTransform: 'uppercase' }}>Amazon →</div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}

              <p style={{ textAlign: 'center', fontFamily: 'DM Mono', fontSize: 10, letterSpacing: 1, color: 'var(--muted)', marginTop: 40 }}>
                OG-TECH LLC may earn a commission on qualifying purchases. Links open Amazon.com.
              </p>
            </div>
          )}

        </div>
      </section>

    </div>
  );
}
